import { useState, useEffect } from 'react';
import { Edit } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateFolder } from '@/hooks/folders';
import { RenderIf } from '@/components/RenderIf';
import type { RenameFolderModalProps } from './types';

const RenameFolderModal = ({
    isOpen,
    onClose,
    folderId,
    folderPath,
    currentName,
    currentPath,
    setCurrentPath,
    agentId,
    entityType,
}: RenameFolderModalProps) => {
    const [folderName, setFolderName] = useState(currentName);
    const { updateFolder, isUpdating, error } = useUpdateFolder({ agentId, entityType });

    // Reset folder name when modal opens with new folder
    useEffect(() => {
        if (isOpen) {
            setFolderName(currentName);
        }
    }, [isOpen, currentName]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const trimmedName = folderName.trim();
        if (!trimmedName || trimmedName === currentName) return;

        const updatedFolder = await updateFolder(folderId, { name: trimmedName });

        const isWithinRenamedFolder =
            currentPath === folderPath || currentPath.startsWith(folderPath + '/');

        if (isWithinRenamedFolder) {
            const childPathSuffix = currentPath.slice(folderPath.length);
            setCurrentPath(updatedFolder.path + childPathSuffix);
        }

        onClose();
    };

    const handleClose = () => {
        setFolderName(currentName);
        onClose();
    };

    const hasChanged = folderName.trim() !== currentName;
    const isSubmitDisabled = !folderName.trim() || !hasChanged || isUpdating;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[425px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="h-5 w-5 text-blue-500" />
                            Rename Folder
                        </DialogTitle>
                        <DialogDescription>Enter a new name for the folder.</DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="folderName">Folder name</Label>
                            <Input
                                id="folderName"
                                placeholder="Enter folder name"
                                value={folderName}
                                onChange={event => setFolderName(event.target.value)}
                                autoFocus
                            />
                        </div>

                        <RenderIf isTrue={!!error}>
                            <p className="text-sm text-red-500">{error?.message}</p>
                        </RenderIf>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitDisabled}>
                            <RenderIf isTrue={isUpdating}>Renaming...</RenderIf>
                            <RenderIf isTrue={!isUpdating}>Rename Folder</RenderIf>
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default RenameFolderModal;
